import { ThemeEvents } from '@theme/events';

/**
 * Sticky Add to cart bar for PDPs.
 * Becomes visible when the main product form ATC button leaves the viewport,
 * and proxies clicks to that button so quantity / selling plans stay in sync.
 */
class StickyAddToCart extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #label = null;

  /** @type {HTMLImageElement | null} */
  #image = null;

  /** @type {HTMLButtonElement | null} */
  #mainButton = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#button = this.querySelector('[data-sticky-atc-button]');
    this.#label = this.querySelector('[data-sticky-atc-label]');
    this.#image = this.querySelector('.sticky-add-to-cart__image');

    this.#mainButton = this.#findMainButton();
    if (!this.#mainButton || !this.#button) return;

    this.#button.addEventListener('click', this.#onClick);
    this.#observeMainButton();
    this.#observeMainButtonState();
    this.#syncFromMain();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.hasAttribute('data-visible')) {
          document.documentElement.style.setProperty('--sticky-atc-offset', `${this.offsetHeight}px`);
        }
      });
      this.#resizeObserver.observe(this);
    }

    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();

    const section = this.closest('.shopify-section');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    document.documentElement.classList.remove('sticky-atc-visible');
    document.documentElement.style.removeProperty('--sticky-atc-offset');
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #findMainButton() {
    const section = this.closest('.shopify-section');
    const scope = section || document;
    return /** @type {HTMLButtonElement | null} */ (
      scope.querySelector('product-form-component [ref="addToCartButton"]')
    );
  }

  #observeMainButton() {
    if (!this.#mainButton) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#setVisible(this.#isPastMainAtc(entry));
      },
      {
        threshold: 0,
      }
    );

    this.#intersectionObserver.observe(this.#mainButton);
  }

  /**
   * Sticky shows only after the main ATC has scrolled up out of view —
   * not when it is still below the fold.
   *
   * @param {IntersectionObserverEntry} entry
   * @returns {boolean}
   */
  #isPastMainAtc(entry) {
    const { top, bottom } = entry.boundingClientRect;
    const viewportHeight = window.innerHeight;

    return !entry.isIntersecting && top < viewportHeight && bottom < viewportHeight;
  }

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));

    if (visible) {
      document.documentElement.style.setProperty('--sticky-atc-offset', `${this.offsetHeight}px`);
      document.documentElement.classList.add('sticky-atc-visible');
    } else {
      document.documentElement.classList.remove('sticky-atc-visible');
      document.documentElement.style.removeProperty('--sticky-atc-offset');
    }
  }

  #observeMainButtonState() {
    if (!this.#mainButton) return;

    this.#mutationObserver = new MutationObserver(() => this.#syncFromMain());
    this.#mutationObserver.observe(this.#mainButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
    });
  }

  #syncFromMain = () => {
    if (!this.#mainButton || !this.#button) return;

    this.#button.disabled = this.#mainButton.disabled;

    const mainLabel = this.#mainButton.querySelector('.add-to-cart-text__content');
    if (mainLabel && this.#label) {
      this.#label.textContent = mainLabel.textContent?.trim() || this.#label.textContent;
    }
  };

  #onClick = () => {
    if (!this.#mainButton || this.#mainButton.disabled) return;
    this.#mainButton.click();
  };

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const resource = event.detail?.resource;
    const mediaSrc = resource?.featured_media?.preview_image?.src;

    if (mediaSrc && this.#image) {
      const url = new URL(mediaSrc, window.location.origin);
      url.searchParams.set('width', '96');
      this.#image.src = url.toString();
    }

    this.#mainButton = this.#findMainButton() || this.#mainButton;
    this.#syncFromMain();
  };
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
